import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, Star, Crown, Zap, ArrowRight, Music, Store, Truck,
  Shield, TrendingUp, BarChart3, Megaphone, Bell, Image
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = "/api";

const TIER_CONFIG = {
  free: {
    icon: Store,
    color: "border-gray-500/30",
    badge: "text-gray-400 bg-gray-500/10 border-gray-500/30",
    btnClass: "bg-gray-600 hover:bg-gray-500",
    accent: "text-gray-400",
    popular: false,
  },
  featured: {
    icon: Star,
    color: "border-yellow-500/40",
    badge: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    btnClass: "bg-yellow-600 hover:bg-yellow-500",
    accent: "text-yellow-400",
    popular: false,
  },
  premium: {
    icon: Crown,
    color: "border-purple-500/40",
    badge: "text-purple-400 bg-purple-500/10 border-purple-500/30",
    btnClass: "bg-purple-600 hover:bg-purple-500",
    accent: "text-purple-400",
    popular: true,
  },
  earlybird: {
    icon: Zap,
    color: "border-emerald-500/40",
    badge: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    btnClass: "bg-emerald-600 hover:bg-emerald-500",
    accent: "text-emerald-400",
    popular: false,
  }
};

const Pricing = () => {
  const [tiers, setTiers] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    email: '',
    business_type: 'vendor',
  });

  useEffect(() => {
    axios.get(`${API}/subscriptions/tiers`)
      .then(res => setTiers(res.data))
      .catch(() => toast.error('Failed to load pricing'));
  }, []);

  const handleSelectTier = (tierId) => {
    if (tierId === 'free') return;
    setSelectedTier(tierId);
    setShowCheckout(true);
  };

  const handleCheckout = async () => {
    if (!form.business_name || !form.email) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        tier_id: selectedTier,
        business_type: form.business_type,
        business_name: form.business_name,
        email: form.email,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  if (!tiers) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-24" data-testid="pricing-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <Badge variant="outline" className="text-purple-400 border-purple-500/30 bg-purple-500/10">
          Support Local Music
        </Badge>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
          Grow Your Business.<br />
          <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Support Local Artists.
          </span>
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
          Every subscription helps Twin Tube sponsor local musicians and strengthen the Twin Cities creative community.
          Play local music in your store or restaurant — harness the power of community.
        </p>
      </motion.div>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {Object.entries(tiers).map(([tierId, tier], i) => {
          const config = TIER_CONFIG[tierId] || TIER_CONFIG.free;
          const TierIcon = config.icon;
          return (
            <motion.div
              key={tierId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="relative"
            >
              {config.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <Badge className="bg-purple-600 text-white border-0 shadow-lg shadow-purple-500/30 text-xs">
                    Most Popular
                  </Badge>
                </div>
              )}
              <Card className={`h-full bg-card/60 backdrop-blur ${config.color} ${config.popular ? 'ring-1 ring-purple-500/40' : ''} transition-all hover:scale-[1.02]`}
                data-testid={`tier-card-${tierId}`}
              >
                <CardHeader className="pb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.badge} border mb-2`}>
                    <TierIcon size={20} />
                  </div>
                  <CardTitle className="text-lg">{tier.name}</CardTitle>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-3xl font-bold">${tier.price.toFixed(0)}</span>
                    {tier.interval && (
                      <span className="text-sm text-muted-foreground">/{tier.interval === 'year' ? 'yr' : 'mo'}</span>
                    )}
                    {!tier.interval && tier.price === 0 && (
                      <span className="text-sm text-muted-foreground">forever</span>
                    )}
                  </div>
                  {tierId === 'earlybird' && (
                    <p className="text-xs text-emerald-400 mt-1">Limited time — lock in this rate forever</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {tier.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check size={14} className={`mt-0.5 flex-shrink-0 ${config.accent}`} />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSelectTier(tierId)}
                    className={`w-full ${tierId === 'free' ? 'bg-gray-700 hover:bg-gray-600' : config.btnClass} text-white font-bold`}
                    disabled={tierId === 'free'}
                    data-testid={`select-tier-${tierId}`}
                  >
                    {tierId === 'free' ? 'Current Plan' : 'Get Started'}
                    {tierId !== 'free' && <ArrowRight size={16} className="ml-2" />}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Value Props */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        {[
          { icon: Music, title: "Support Local Artists", desc: "Subscriptions sponsor musicians and help them take their craft further." },
          { icon: TrendingUp, title: "Grow Your Reach", desc: "Get discovered by the Twin Cities community on the platform they already use." },
          { icon: Shield, title: "Community Powered", desc: "Play local music in your store — connect with your neighborhood." },
        ].map((prop, i) => (
          <Card key={i} className="bg-card/30 border-border/30">
            <CardContent className="p-4 flex gap-3 items-start">
              <div className="shrink-0 w-9 h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                <prop.icon size={18} className="text-purple-400" />
              </div>
              <div>
                <p className="font-bold text-sm">{prop.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{prop.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </motion.div>

      {/* Additional Revenue Streams Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="space-y-4"
      >
        <h2 className="font-heading text-2xl font-bold text-center">Additional Opportunities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="bg-card/40 border-border/40">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2">
                <Megaphone size={18} className="text-blue-400" />
                <h3 className="font-bold">Event Sponsorship</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Premium and Early Bird subscribers can sponsor events — your business name on event cards seen by the whole community.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/40 border-border/40">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2">
                <Truck size={18} className="text-orange-400" />
                <h3 className="font-bold">Book for Your Event</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Food trucks with paid tiers get a "Book for Your Event" button — weddings, festivals, parties, and corporate catering leads.
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedTier && tiers[selectedTier] ? `Subscribe to ${tiers[selectedTier].name}` : 'Subscribe'}
            </DialogTitle>
            <DialogDescription>
              {selectedTier && tiers[selectedTier] && (
                <span>${tiers[selectedTier].price.toFixed(2)}/{tiers[selectedTier].interval === 'year' ? 'year' : 'month'}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                placeholder="Your business name"
                value={form.business_name}
                onChange={e => setForm({...form, business_name: e.target.value})}
                data-testid="checkout-business-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@business.com"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                data-testid="checkout-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Type</Label>
              <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})}>
                <SelectTrigger data-testid="checkout-business-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor">Shop / Vendor</SelectItem>
                  <SelectItem value="food-truck">Food Truck</SelectItem>
                  <SelectItem value="venue">Venue</SelectItem>
                  <SelectItem value="dispensary">Dispensary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold"
              data-testid="checkout-submit"
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pricing;
